import { Button } from '@mui/material'
import { Box, Stack } from '@mui/system'
import { inject, observer } from 'mobx-react'
import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useLocation } from 'react-router'
import { queryClient } from '../../../../../App'
import DataUnit from '../../../../../components/customElements/DataUnit/DataUnit'
import FormInput from '../../../../../components/customElements/FormInput'
import MultipleSelect from '../../../../../components/customElements/MultipleSelect/MultipleSelect'
import CustomAccordion from '../../../../../components/customElements/RequestDataAccordions/utils/CustomAccordion'
import { formatAddress, formatName } from '../../../../../helpers/ordersUtiles'
import { isAdmin } from '../../../../../services/AccountService'
import requestsManagementStore from '../../../../../stores/RequestsManagementStore'
import { getComp } from '../../../../../utils/utils/DI'
import { useEditData } from './useEditData'
import { useReportEdit } from './useReportEdit'

const AddServiceRequestForm = ({
  serviceStore,
  alwaysShow,
  onEdit,
  isEdit,
  setIsEdit,
  withoutAdditionalInfo,
  isReportStatus,
}) => {
  const [isValidFormState, setIsValidFormState] = useState(true)
  const intl = useIntl()
  const { pathname } = useLocation()
  const { operatorStore } = requestsManagementStore
  const categoriesStore = getComp('CategoriesStore')
  const {
    showAddServiceRequestForm,
    serviceAddRequestFormStore,
    currentServiceData,
    updateServiceOrder,
    showRequestPreview,
    serviceAddRequestFormStore: { data: formData },
    updateOrders,
    updateCurrentServiceData,
  } = serviceStore

  const { mutateAsync, isReportEdit } = useReportEdit(currentServiceData)
  const { editData, isLocationUpdated } = useEditData(
    currentServiceData,
    serviceAddRequestFormStore,
    isEdit,
    isReportEdit
  )

  useEffect(() => {
    if (!isLocationUpdated()) serviceAddRequestFormStore.clearError('locality')
    // Clear errors not related to properties available for editing
    // Otherwise, the form store state is not valid due to properties not involved in editing.
    Object.keys(serviceAddRequestFormStore.errors)
      .filter(
        (key) => !editData.some((editableProp) => editableProp.propName === key)
      )
      .forEach((key) => serviceAddRequestFormStore.clearError(key))

    setIsValidFormState(serviceAddRequestFormStore.isValid)
  }, [serviceAddRequestFormStore.isValid])

  const handleUpdateOrder = async () => {
    if (isReportStatus) {
      const res = await updateServiceOrder(mutateAsync)

      if (isAdmin() && pathname === '/service') {
        queryClient.invalidateQueries({
          queryKey: ['service-table'],
        })
        updateCurrentServiceData(res)
      } else {
        updateOrders(res)
      }
      setIsEdit(false)
    } else {
      updateServiceOrder(
        operatorStore?.selectedTab && operatorStore?.selectedTab === 3
      ).then(() => {
        queryClient.invalidateQueries({
          queryKey: ['service-table'],
        })
        queryClient.invalidateQueries({
          queryKey: ['get-service-history'],
        })
        // это нужно для вызова rework на стр уточнения по ремонтам
        setIsEdit(false)
      })
    }
  }

  const handleEdit = () => {
    setIsEdit((prev) => !prev)
  }

  if (showAddServiceRequestForm || alwaysShow) {
    return (
      <Box
        mt={alwaysShow ? 0 : 2}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          height: '100%',
        }}
      >
        <Box>
          <CustomAccordion
            summary={alwaysShow ? 'Редактирование данных' : 'Данные Абонента'}
            defaultOpenState={true}
            preventHandleOpen={true}
            onEdit={onEdit ? handleEdit : null}
          >
            {editData.map((el, i) => {
              const {
                propName,
                validName,
                label,
                items,
                mask,
                defaultValue,
                autocompleteOptions,
                multiple,
                readOnly,
                disabled,
                type,
                visible,
                territorySelect,
                onClear,
                capitalizeFirstLetter,
                styles,
                multiline,
                paste,
                onlyNumbers,
                onlyNumbersAndLetters,
              } = el

              if (withoutAdditionalInfo && propName === 'additionalInfo') {
                return
              }

              if (
                [20, 21, 24].includes(currentServiceData.status) &&
                !serviceCenterFields.includes(propName)
              ) {
                return
              }

              if (
                ![20, 21, 24].includes(currentServiceData.status) &&
                propName === 'serviceCenterComment' &&
                isEdit
              ) {
                return
              }

              return (
                <Box
                  mt={i ? 1.5 : 0}
                  key={i + 'createOrderInput'}
                  sx={{ marginTop: '12px' }}
                >
                  {multiple ? (
                    <MultipleSelect
                      items={items}
                      value={
                        serviceAddRequestFormStore.data.preferentialCategories
                      }
                      onChange={serviceAddRequestFormStore.onChangeMultiple}
                      label={label}
                      propName={propName}
                      validName={validName}
                      readOnly={readOnly}
                      disabled={disabled}
                      onClear={onClear}
                    />
                  ) : (
                    <Box mt={i ? 1.5 : 0} key={i + ''}>
                      <FormInput
                        api={serviceAddRequestFormStore}
                        type={type}
                        autocompleteOptions={autocompleteOptions}
                        items={!!items ? items : null}
                        propName={propName}
                        validName={validName}
                        fullWidth
                        defaultValue={defaultValue}
                        mask={mask}
                        label={label}
                        withoutErrorText
                        readOnly={readOnly}
                        disabled={disabled}
                        visible={visible}
                        territorySelect={territorySelect}
                        capitalizeFirstLetter={capitalizeFirstLetter}
                        styles={styles}
                        multiline={multiline}
                        paste={paste}
                        onlyNumbers={onlyNumbers}
                        onlyNumbersAndLetters={onlyNumbersAndLetters}
                      />
                    </Box>
                  )}
                </Box>
              )
            })}
            {alwaysShow && (
              <Box className={'jst-sa'} sx={{ gap: '8px', marginTop: '14px' }}>
                <Button
                  disabled={!isValidFormState}
                  onClick={handleUpdateOrder}
                  color={'primary'}
                  variant={'contained'}
                  fullWidth
                >
                  {intl.formatMessage({ id: 'Применить изменения' })}
                </Button>
                <Button
                  onClick={() => setIsEdit(false)}
                  color={'primary'}
                  variant={'contained'}
                  fullWidth
                >
                  {intl.formatMessage({ id: 'Отмена' })}
                </Button>
              </Box>
            )}
          </CustomAccordion>
        </Box>
      </Box>
    )
  } else if (showRequestPreview) {
    return (
      <Box
        mt={alwaysShow ? 0 : 2}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          height: '100%',
        }}
      >
        <Box>
          <CustomAccordion
            summary={alwaysShow ? 'Редактирование данных' : 'Данные Абонента'}
            defaultOpenState={true}
            preventHandleOpen={true}
            onEdit={onEdit ? handleEdit : null}
          >
            <Stack spacing={'10px'}>
              {editData.map((el, i) => {
                const arr = [
                  'name',
                  'patronymic',
                  'district',
                  'locality',
                  'address',
                  'house',
                  'building',
                  'addressType',
                  'apartment',
                ]
                if (arr.includes(el.propName)) {
                  return
                }

                if (
                  currentServiceData.status !== 24 &&
                  propName === 'serviceCenterComment'
                ) {
                  return
                }

                if (el.propName === 'surname') {
                  return (
                    <DataUnit
                      name={'ФИО'}
                      value={formatName(formData) || '-'}
                      key={el.propName}
                    />
                  )
                }

                if (el.propName === 'territory') {
                  return (
                    <DataUnit
                      name={'Адрес'}
                      value={formatAddress(formData) || '-'}
                      key={el.propName}
                    />
                  )
                }

                if (el.propName === 'preferentialCategories') {
                  return (
                    <DataUnit
                      name={el.label}
                      value={
                        formData[el.propName].length
                          ? categoriesStore?.formatCategory(
                              formData[el.propName]
                            )
                          : '-'
                      }
                      key={el.propName}
                    />
                  )
                }
                return (
                  <DataUnit
                    name={el.label}
                    value={formData[el.propName] || '-'}
                    key={el.propName}
                  />
                )
              })}
            </Stack>
          </CustomAccordion>
        </Box>
      </Box>
    )
  }
}

const serviceCenterFields = [
  'surname',
  'name',
  'patronymic',
  'phoneNumber',
  'territory',
  'district',
  'locality',
  'serviceCenterComment',
  'address',
  'addressType',
  'building',
  'house',
  'apartment',
]

export default inject('serviceStore')(observer(AddServiceRequestForm))
